import { useCallback, useEffect } from 'react';

export default function usePdfSelectionInteractions({
  containerRef,
  textActionsRef,
  isReadingRef,
  showTextActions,
  selectionRangeRef,
  buildSelectionAnchorFromRange,
  clearTextSelection,
  setShowTextActions,
  setSelectionRects,
  setSelectedText,
  setSelectionAnchor,
  setTextActionPosition,
  isReading,
}) {
  const handleHighlightedTextClick = useCallback((e) => {
    if (e.target.classList.contains('highlight-remove-btn')) {
      return;
    }

    const highlightedSpan = e.target.closest('span[data-highlight-id]');
    if (highlightedSpan) {
      setShowTextActions(false);
      e.stopPropagation();
      window.getSelection()?.removeAllRanges();
    }
  }, [setShowTextActions]);

  useEffect(() => {
    if (!showTextActions) return;

    const handleScroll = () => {
      if (!isReadingRef.current) {
        clearTextSelection('scroll');
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [showTextActions, clearTextSelection, containerRef, isReadingRef]);

  useEffect(() => {
    const handleTextSelection = (e) => {
      console.log('🎯 Selection event fired');

      if (isReadingRef.current) {
        console.log('🔊 Currently reading, ignoring selection event');
        return;
      }

      if (e && e.target && e.target.closest('.text-action-buttons')) {
        console.log('🚫 Ignoring click on action buttons');
        return;
      }

      const selection = window.getSelection();
      const text = selection?.toString()?.trim() || '';

      console.log('📝 Selected text:', text);

      if (!text) {
        console.log('✅ Empty selection in handler; leaving existing UI/state as-is');
        return;
      }

      const range = selection.getRangeAt(0);
      const pdfContainer = containerRef.current;

      console.log('📦 PDF Container:', pdfContainer);
      console.log('🎯 Range:', range);

      if (!pdfContainer?.contains(range.commonAncestorContainer)) {
        console.log('❌ Selection not in PDF container');
        return;
      }

      if (range.commonAncestorContainer.parentElement?.closest('[data-highlight-id]')) {
        console.log('❌ Selection on highlighted text');
        return;
      }

      const rect = range.getBoundingClientRect();
      console.log('📐 Rect:', rect);

      if (rect.width > 0 && rect.height > 0) {
        selectionRangeRef.current = range.cloneRange();
        const rects = Array.from(range.getClientRects())
          .filter((r) => r && r.width > 0 && r.height > 0)
          .map((r) => ({ top: r.top, left: r.left, width: r.width, height: r.height }));

        console.log('✅ Setting selection state with', rects.length, 'rects');
        setSelectionRects(rects);
        setSelectedText(text);
        setSelectionAnchor(buildSelectionAnchorFromRange(range, text));
        setTextActionPosition({
          top: rect.bottom + window.scrollY + 5,
          left: rect.left + window.scrollX + (rect.width / 2),
        });
        setShowTextActions(true);
        console.log('✅ Selection UI should now be visible');
      } else {
        console.log('❌ Invalid rect dimensions');
      }
    };

    const handleClickOutside = (e) => {
      console.log('🖱️ Click outside event', e.target);
      if (isReadingRef.current) {
        console.log('🔊 Currently reading, keeping selection');
        return;
      }

      if (textActionsRef.current && textActionsRef.current.contains(e.target)) {
        console.log('🚫 Click inside text actions (via ref check)');
        return;
      }

      // Don't clear selection if click is in chat panel or textarea
      if (e.target.closest('textarea') || 
        e.target.closest('.budlee-chat-panel') ||
        e.target.closest('[id="right-panel-resize-container"]')) {
        console.log('💬 Click in chat panel; ignoring selection clear');
        return;
      }

      if (!e.target.closest('.text-actions-panel') &&
        !e.target.closest('.text-action-buttons') &&
        !e.target.closest('.react-pdf__Page__textContent') &&
        !e.target.closest('.notes-view-viewer')) {
        console.log('❌ Clearing selection from click outside');
        clearTextSelection('clickOutside');
      }
    };

    const handleContextMenu = (e) => {
      if (window.innerWidth < 768) {
        e.preventDefault();
      }
    };

    const pdfContainer = containerRef.current;
    if (pdfContainer) {
      pdfContainer.addEventListener('click', handleHighlightedTextClick);
      pdfContainer.addEventListener('contextmenu', handleContextMenu);
    }

    document.addEventListener('mouseup', handleTextSelection);
    document.addEventListener('touchend', handleTextSelection);
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('mouseup', handleTextSelection);
      document.removeEventListener('touchend', handleTextSelection);
      document.removeEventListener('click', handleClickOutside);
      const currentPdfContainer = containerRef.current;
      if (currentPdfContainer) {
        currentPdfContainer.removeEventListener('click', handleHighlightedTextClick);
        currentPdfContainer.removeEventListener('contextmenu', handleContextMenu);
      }
    };
  }, [
    buildSelectionAnchorFromRange,
    clearTextSelection,
    containerRef,
    handleHighlightedTextClick,
    isReading,
    isReadingRef,
    showTextActions,
    selectionRangeRef,
    setSelectionAnchor,
    setSelectedText,
    setSelectionRects,
    setShowTextActions,
    setTextActionPosition,
    textActionsRef,
  ]);
}
